import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import ScrollToTop from "./components/ScrollToTop";
import Index from "./pages/Index";
import TripPlanner from "./pages/TripPlanner";
import Destinations from "./pages/Destinations";
import DestinationDetail from "./pages/DestinationDetail";
import Media from "./pages/Media";
import MyTrips from "./pages/MyTrips";
import MyPassport from "./pages/MyPassport";
import MySwellBreaks from "./pages/MySwellBreaks";
import SurfingProgression from "./pages/SurfingProgression";
import Auth from "./pages/Auth";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsAndConditions from "./pages/TermsAndConditions";
import NotFound from "./pages/NotFound";
import SeasonAbilityGraphDev from "./pages/dev/SeasonAbilityGraphDev";
import MySwellBreaksDev from "./pages/dev/MySwellBreaksDev";
import { AuthProvider } from "./contexts/AuthContext";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Default staleTime is 0, which makes every component mount (e.g. every
      // route navigation) treat already-fetched data as stale and refetch it
      // in the background -- measured to be firing the same ~58KB
      // destinations query on every single page (homepage, destinations,
      // destination detail, trip planner), each a live round trip to
      // Supabase. Mutations already call invalidateQueries explicitly
      // (see usePassport.ts, useSavedDestinations.ts), so a nonzero
      // staleTime only affects automatic background refetching, not
      // correctness after a user's own change.
      staleTime: 60 * 1000,
    },
  },
});

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <ScrollToTop />
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/trip-planner" element={<TripPlanner />} />
            <Route path="/destinations" element={<Destinations />} />
            <Route path="/destinations/:destinationId" element={<DestinationDetail />} />
            <Route path="/media" element={<Media />} />
            <Route path="/my-trips" element={<MyTrips />} />
            <Route path="/my-passport" element={<MyPassport />} />
            <Route path="/my-swell-breaks" element={<MySwellBreaks />} />
            <Route path="/my-swell-breaks/progression" element={<SurfingProgression />} />
            <Route path="/auth" element={<Auth />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
            <Route path="/dev/season-ability" element={<SeasonAbilityGraphDev />} />
            <Route path="/dev/my-swell-breaks" element={<MySwellBreaksDev />} />
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </BrowserRouter>
      </TooltipProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;
